import type { LeaderboardEntry } from '@wooorms/shared';

/**
 * Arcade-style rank badges
 */
const RANK_BADGES = {
  1: { emoji: '🥇', color: '#ffd700', glow: 'rgba(255, 215, 0, 0.5)' },
  2: { emoji: '🥈', color: '#c0c0c0', glow: 'rgba(192, 192, 192, 0.4)' },
  3: { emoji: '🥉', color: '#cd7f32', glow: 'rgba(205, 127, 50, 0.4)' },
} as const;

/**
 * LeaderboardUI - Retro Arcade Style High Score Board
 *
 * Features:
 * - Arcade cabinet styling with neon glow effects
 * - Top 3 players get medal badges
 * - Current player highlighted with animated border
 * - Score display with arcade-style formatting
 * - Differential updates to avoid unnecessary DOM rebuilds
 */
export class LeaderboardUI {
  /** Container element */
  private container: HTMLDivElement;

  /** List element for entries */
  private listElement: HTMLOListElement;

  /** Current local player ID */
  private localPlayerId: string | null = null;

  /** Whether UI is visible */
  private visible: boolean = false;

  /** Cache of previous entries for differential updates */
  private cachedEntries: LeaderboardEntry[] = [];

  /** Whether the leaderboard is minimized */
  private minimized: boolean = false;

  /** The list container element */
  private listContainer: HTMLDivElement | null = null;

  /** The minimize button element */
  private minimizeBtn: HTMLButtonElement | null = null;

  constructor() {
    // Inject keyframe animations
    this.injectStyles();

    // Create container with arcade cabinet styling
    // Positioned below the minimap (which is ~200px tall + 15px padding)
    this.container = document.createElement('div');
    this.container.id = 'leaderboard-ui';
    this.container.style.cssText = `
      position: fixed;
      top: 230px;
      right: 10px;
      width: 220px;
      background: linear-gradient(180deg, rgba(15, 15, 35, 0.95) 0%, rgba(10, 10, 25, 0.95) 100%);
      border-radius: 12px;
      padding: 0;
      font-family: 'Segoe UI', Arial, sans-serif;
      z-index: 100;
      display: none;
      box-shadow:
        0 0 20px rgba(78, 205, 196, 0.3),
        0 4px 15px rgba(0, 0, 0, 0.5),
        inset 0 1px 0 rgba(255, 255, 255, 0.1);
      border: 2px solid rgba(78, 205, 196, 0.5);
      overflow: hidden;
      transition: height 0.2s ease-out;
    `;

    // Title bar with arcade styling - clickable to minimize
    const titleBar = document.createElement('div');
    titleBar.style.cssText = `
      background: linear-gradient(90deg, rgba(78, 205, 196, 0.3) 0%, rgba(78, 205, 196, 0.1) 50%, rgba(78, 205, 196, 0.3) 100%);
      padding: 10px 12px;
      border-bottom: 1px solid rgba(78, 205, 196, 0.3);
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;
    titleBar.addEventListener('click', () => this.toggleMinimize());

    const title = document.createElement('h3');
    title.innerHTML = '🏆 <span style="letter-spacing: 2px;">HIGH SCORES</span>';
    title.style.cssText = `
      color: #4ecdc4;
      font-size: 13px;
      font-weight: bold;
      margin: 0;
      text-shadow: 0 0 10px rgba(78, 205, 196, 0.8);
      text-transform: uppercase;
    `;

    // Minimize button
    this.minimizeBtn = document.createElement('button');
    this.minimizeBtn.innerHTML = '−';
    this.minimizeBtn.style.cssText = `
      background: none;
      border: none;
      color: #4ecdc4;
      font-size: 18px;
      font-weight: bold;
      cursor: pointer;
      padding: 0 5px;
      line-height: 1;
      opacity: 0.7;
      transition: opacity 0.2s;
    `;
    this.minimizeBtn.addEventListener('mouseenter', () => {
      if (this.minimizeBtn) this.minimizeBtn.style.opacity = '1';
    });
    this.minimizeBtn.addEventListener('mouseleave', () => {
      if (this.minimizeBtn) this.minimizeBtn.style.opacity = '0.7';
    });

    titleBar.appendChild(title);
    titleBar.appendChild(this.minimizeBtn);
    this.container.appendChild(titleBar);

    // List container with padding
    this.listContainer = document.createElement('div');
    this.listContainer.style.cssText = `
      padding: 8px 10px 10px 10px;
      transition: max-height 0.2s ease-out, opacity 0.2s ease-out;
      overflow: hidden;
    `;

    // List
    this.listElement = document.createElement('ol');
    this.listElement.style.cssText = `
      list-style: none;
      margin: 0;
      padding: 0;
    `;
    this.listContainer.appendChild(this.listElement);
    this.container.appendChild(this.listContainer);

    document.body.appendChild(this.container);
  }

  /**
   * Inject CSS animations for arcade effects
   */
  private injectStyles(): void {
    if (document.getElementById('leaderboard-styles')) return;

    const style = document.createElement('style');
    style.id = 'leaderboard-styles';
    style.textContent = `
      @keyframes leaderboard-pulse {
        0%, 100% { box-shadow: 0 0 5px rgba(78, 205, 196, 0.5); }
        50% { box-shadow: 0 0 15px rgba(78, 205, 196, 0.8); }
      }
      @keyframes leaderboard-glow {
        0%, 100% { text-shadow: 0 0 5px currentColor; }
        50% { text-shadow: 0 0 15px currentColor, 0 0 25px currentColor; }
      }
      .leaderboard-entry:hover {
        background: rgba(78, 205, 196, 0.15) !important;
        transform: translateX(3px);
      }
      .leaderboard-local {
        animation: leaderboard-pulse 2s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Set the local player ID for highlighting
   */
  setLocalPlayerId(playerId: string | null): void {
    this.localPlayerId = playerId;
  }

  /**
   * Toggle the minimized state of the leaderboard
   */
  toggleMinimize(): void {
    this.minimized = !this.minimized;

    if (this.listContainer) {
      if (this.minimized) {
        this.listContainer.style.maxHeight = '0';
        this.listContainer.style.opacity = '0';
        this.listContainer.style.padding = '0 10px';
      } else {
        this.listContainer.style.maxHeight = '500px';
        this.listContainer.style.opacity = '1';
        this.listContainer.style.padding = '8px 10px 10px 10px';
      }
    }

    if (this.minimizeBtn) {
      this.minimizeBtn.innerHTML = this.minimized ? '+' : '−';
    }
  }

  /**
   * Check if entries have changed (compare by id, name, score, and order)
   */
  private hasEntriesChanged(entries: LeaderboardEntry[]): boolean {
    if (entries.length !== this.cachedEntries.length) return true;

    for (let i = 0; i < entries.length; i++) {
      const newEntry = entries[i];
      const oldEntry = this.cachedEntries[i];
      if (!newEntry || !oldEntry) return true;
      if (newEntry.id !== oldEntry.id ||
          newEntry.name !== oldEntry.name ||
          newEntry.score !== oldEntry.score ||
          newEntry.rank !== oldEntry.rank) {
        return true;
      }
    }
    return false;
  }

  /**
   * Update the leaderboard display with arcade-style entries
   * Uses differential updates to skip DOM rebuilds when nothing changed
   */
  update(entries: LeaderboardEntry[]): void {
    // Skip update if entries haven't changed
    if (!this.hasEntriesChanged(entries)) {
      return;
    }

    // Cache the new entries (shallow copy)
    this.cachedEntries = entries.map(e => ({ ...e }));

    // Clear existing entries
    this.listElement.innerHTML = '';

    // Add entries with arcade styling
    entries.forEach((entry, index) => {
      const rank = index + 1;
      const isLocal = entry.id === this.localPlayerId;
      const badge = RANK_BADGES[rank as keyof typeof RANK_BADGES];

      const li = document.createElement('li');
      li.className = 'leaderboard-entry';
      if (isLocal) li.classList.add('leaderboard-local');

      // Build entry background based on rank and local status
      let bgColor = 'transparent';
      let borderColor = 'transparent';
      let glowEffect = '';

      if (isLocal) {
        bgColor = 'rgba(78, 205, 196, 0.2)';
        borderColor = '#4ecdc4';
      } else if (badge) {
        bgColor = `${badge.glow.replace(')', ', 0.15)')}`;
        borderColor = badge.color;
        glowEffect = `box-shadow: inset 0 0 10px ${badge.glow};`;
      }

      li.style.cssText = `
        display: flex;
        align-items: center;
        padding: 5px 8px;
        margin: 3px 0;
        border-radius: 6px;
        font-size: 12px;
        background: ${bgColor};
        border-left: 3px solid ${borderColor};
        transition: all 0.15s ease;
        ${glowEffect}
      `;

      // Rank display (badge emoji for top 3, number otherwise)
      const rankSpan = document.createElement('span');
      rankSpan.style.cssText = `
        width: 24px;
        text-align: center;
        font-size: ${badge ? '14px' : '11px'};
        color: ${badge ? badge.color : '#666'};
        text-shadow: ${badge ? `0 0 8px ${badge.glow}` : 'none'};
        flex-shrink: 0;
      `;
      rankSpan.textContent = badge ? badge.emoji : `${rank}.`;

      // Name container
      const nameSpan = document.createElement('span');
      nameSpan.style.cssText = `
        color: ${isLocal ? '#4ecdc4' : badge ? badge.color : '#ccc'};
        font-weight: ${isLocal || badge ? 'bold' : 'normal'};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        margin-left: 4px;
        text-shadow: ${isLocal ? '0 0 8px rgba(78, 205, 196, 0.6)' : badge ? `0 0 6px ${badge.glow}` : 'none'};
      `;
      nameSpan.textContent = entry.name;
      if (isLocal) {
        const youTag = document.createElement('span');
        youTag.style.cssText = `
          font-size: 9px;
          color: #4ecdc4;
          background: rgba(78, 205, 196, 0.3);
          padding: 1px 4px;
          border-radius: 3px;
          margin-left: 4px;
          text-shadow: none;
        `;
        youTag.textContent = 'YOU';
        nameSpan.appendChild(youTag);
      }

      // Score with arcade-style formatting
      const scoreSpan = document.createElement('span');
      scoreSpan.style.cssText = `
        color: ${isLocal ? '#4ecdc4' : badge ? badge.color : '#888'};
        font-weight: bold;
        font-size: 11px;
        font-family: 'Courier New', monospace;
        text-shadow: ${badge ? `0 0 6px ${badge.glow}` : 'none'};
        min-width: 45px;
        text-align: right;
      `;
      scoreSpan.textContent = this.formatScore(entry.score);

      li.appendChild(rankSpan);
      li.appendChild(nameSpan);
      li.appendChild(scoreSpan);
      this.listElement.appendChild(li);
    });

    // Show arcade-style message if empty
    if (entries.length === 0) {
      const empty = document.createElement('li');
      empty.style.cssText = `
        color: #4ecdc4;
        font-size: 11px;
        text-align: center;
        padding: 15px 10px;
        text-transform: uppercase;
        letter-spacing: 1px;
        opacity: 0.7;
      `;
      empty.innerHTML = '⏳ WAITING FOR PLAYERS...';
      this.listElement.appendChild(empty);
    }
  }

  /**
   * Format score for display
   */
  private formatScore(score: number): string {
    if (score >= 1000000) {
      return (score / 1000000).toFixed(1) + 'M';
    } else if (score >= 1000) {
      return (score / 1000).toFixed(1) + 'K';
    }
    return score.toString();
  }

  /**
   * Show the leaderboard
   */
  show(): void {
    this.visible = true;
    this.container.style.display = 'block';
  }

  /**
   * Hide the leaderboard
   */
  hide(): void {
    this.visible = false;
    this.container.style.display = 'none';
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Clean up
   */
  destroy(): void {
    this.container.remove();
  }
}
